import {
  Fit,
  Layout,
  useRive,
  useViewModelInstanceNumber,
  useViewModelInstanceTrigger,
} from '@rive-app/react-canvas'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import riv from '~/features/multi-page/assets/rive/paywall_feature.riv'
import { pageVisibleOnce } from '~/loading'
import { m } from '~/paraglide/messages'
import { native } from '@forest/native-services'
import { PlusBadge } from './icons/plus-badge'

const sceneNumbersByVariant = {
  cn_region_default: [2, 1, 4],
  others_default: [2, 1, 3],
  cn_region_trees: [1, 2, 4],
  others_trees: [1, 2, 3],
  real_tree: [3, 2, 1],
  coin_3x: [4, 2, 1],
  pro_china: [1, 4],
  pro_others: [1, 4],
  pro_coin_3x: [4, 1],
} as const

export type FeaturesPageVariant = keyof typeof sceneNumbersByVariant

export function FeaturesPage(props: {
  next: () => void
  variant: keyof typeof sceneNumbersByVariant
}) {
  const sceneNumbers = sceneNumbersByVariant[props.variant]

  const [riveSceneIndex, setRiveSceneIndex] = useState(0)
  const [isTitleVisible, setIsTitleVisible] = useState(false)
  const [isButtonVisible, setIsButtonVisible] = useState(false)
  const isButtonDisabledRef = useRef(true)

  const sceneNumber = sceneNumbers[riveSceneIndex]
  useEffect(() => {
    const pageNameBySceneNumber = {
      1: 'trial_paywall_exclusive_trees',
      2: 'trial_paywall_allowlist',
      3: 'trial_paywall_realtree',
      4: 'trial_paywall_coin_3x',
    }
    pageVisibleOnce.promise.then(() => {
      native.general.logEvent({
        event_name: 'page_view',
        event_params: {
          page_name: pageNameBySceneNumber[sceneNumber],
        },
      })
    })
  }, [sceneNumber])

  const { rive, RiveComponent } = useRive({
    src: riv,
    autoBind: true,
    artboard: 'paywall_feature',
    autoplay: true,
    layout: new Layout({ fit: Fit.FitHeight }),
    stateMachines: ['State Machine 1'],
    onStateChange: (event) => {
      let [state] = event.data as [string]
      state = state.toLowerCase()
      if (state.includes('entry')) {
        setIsTitleVisible(true)
      }
      if (state.includes('idle')) {
        setIsButtonVisible(true)
        isButtonDisabledRef.current = false
      }
    },
  })

  const sceneNumInput = useViewModelInstanceNumber(
    'numSlot',
    rive?.viewModelInstance,
  )
  const nextRiveTrigger = useViewModelInstanceTrigger(
    'trigClick',
    rive?.viewModelInstance,
  )

  const hasInitialRivePlayedRef = useRef(false)
  useEffect(() => {
    if (sceneNumInput.value === null) return
    if (hasInitialRivePlayedRef.current) return
    hasInitialRivePlayedRef.current = true
    sceneNumInput.setValue(sceneNumbers[0])
  }, [sceneNumInput])

  const buttonGuardRef = useRef(true)
  const handleButtonClick = () => {
    if (isButtonDisabledRef.current) return
    if (riveSceneIndex < sceneNumbers.length - 1) {
      isButtonDisabledRef.current = true
      setIsTitleVisible(false)
      setIsButtonVisible(false)
      sceneNumInput?.setValue(sceneNumbers[riveSceneIndex + 1])
      nextRiveTrigger?.trigger()
      setRiveSceneIndex((prev) => prev + 1)
    } else {
      if (!buttonGuardRef.current) return
      props.next()
      buttonGuardRef.current = false
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative h-full overflow-hidden px-6 pt-[calc(32px+var(--safe-area-top))]"
    >
      <div className="flex items-center justify-center gap-2">
        <span className="text-lg font-semibold text-white">Forest</span>
        <PlusBadge />
      </div>
      <div className="mt-3 flex h-[108px] items-center justify-center">
        <AnimatePresence mode="wait" initial={false}>
          {isTitleVisible && (
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              key={riveSceneIndex}
              className="whitespace-pre-line text-center text-[26px] font-bold text-white"
              style={{
                textShadow: '0 0 20.6px rgba(187, 245, 207, 0.47)',
              }}
              dangerouslySetInnerHTML={{
                __html:
                  sceneTitleByNumber[sceneNumbers[riveSceneIndex]]?.replace(
                    '@@',
                    'color: #00FF00;',
                  ) ?? '',
              }}
            />
          )}
        </AnimatePresence>
      </div>

      <div className="relative mt-3 h-[65vh]" key="rive">
        <RiveComponent className="absolute left-1/2 top-0 h-full w-screen -translate-x-1/2" />
      </div>

      <AnimatePresence>
        {isButtonVisible && (
          <motion.div
            key={String(riveSceneIndex > 0)}
            id="footer"
            className="fixed bottom-0 left-0 right-0 z-30 bg-[#001721] px-6 pb-12 pt-3 opacity-0"
            style={{
              boxShadow: '0 -3px 3px 0 rgba(255, 255, 255, 0.20)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="mx-auto flex w-full max-w-[640px] flex-col items-center space-y-3">
              <button
                className="h-12 w-full rounded-lg bg-gradient-to-r from-[#81D200] to-[#08C1F3] font-bold text-[#06261D]"
                style={{
                  boxShadow: 'inset 0px 0px 28px rgba(182, 255, 164, 1)',
                }}
                onClick={handleButtonClick}
              >
                {riveSceneIndex > 0
                  ? m.btn_try_for_free()
                  : m.btn_zero_fee_trial()}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

const sceneTitleByNumber: Record<number, string> = {
  1: m.feature_animation_exclusive_tree_title(),
  2: m.feature_animation_allowlist_title(),
  3: m.feature_animation_real_tree_title(),
  4: m.feature_animation_triple_coin_title(),
}
